import { useState, useEffect } from "react";
import { COLORS, TYPOGRAPHY, SPACING, RADIUS, SHADOWS, TRANSITIONS, GLASSMORPHISM } from '../constants/designTokens';
import { CATEGORIES } from '../constants/siteConfig';

/**
 * Navbar component renders the main navigation bar for the website.
 *
 * - Uses Bootstrap classes for responsive layout and styling.
 * - Custom styles are injected for a marine-themed appearance.
 * - Includes a brand logo and navigation links: Home, About, Services, Contact.
 * - Features a responsive navbar toggler for mobile view.
 * - Navigation links have animated underline and color transition on hover/focus.
 *
 * @component
 * @returns {JSX.Element} The rendered Navbar component.
 */


const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [scrolled]);

  return (
  <>
    <style>{`
      .navbar-marine {
        background: ${COLORS.primary.dark} !important;
        transition: ${TRANSITIONS.all};
        position: fixed;
        top: 0;
        width: 100%;
        z-index: 1030;
      }
      .navbar-marine.scrolled {
        background: ${GLASSMORPHISM.navbar.background} !important;
        backdrop-filter: ${GLASSMORPHISM.navbar.backdropFilter};
        -webkit-backdrop-filter: ${GLASSMORPHISM.navbar.WebkitBackdropFilter};
        border-bottom: ${GLASSMORPHISM.navbar.borderBottom};
        box-shadow: ${GLASSMORPHISM.navbar.boxShadow};
      }
      .navbar-marine .navbar-brand,
      .navbar-marine .nav-link {
        color: ${COLORS.text.white} !important;
        transition: ${TRANSITIONS.colors};
      }
      .navbar-marine .nav-link {
        position: relative;
        margin-left: ${SPACING.md};
        margin-right: ${SPACING.md};
        font-weight: ${TYPOGRAPHY.fontWeight.medium};
        font-size: ${TYPOGRAPHY.fontSize.md};
      }
      .navbar-marine .nav-link.dropdown-toggle::after {
        display: none;
      }
      .navbar-marine .nav-link.dropdown-toggle {
        padding-right: ${SPACING.lg};
      }
      .navbar-marine .nav-link.dropdown-toggle::before {
        content: '';
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 6px solid ${COLORS.text.white};
        transition: ${TRANSITIONS.all};
        opacity: 0.8;
      }
      .navbar-marine .nav-link.dropdown-toggle:hover::before {
        border-top-color: ${COLORS.primary.teal};
        transform: translateY(-50%) translateY(-2px);
        opacity: 1;
      }
      .navbar-marine .dropdown:hover .dropdown-toggle::before,
      .navbar-marine .dropdown.show .dropdown-toggle::before {
        transform: translateY(-50%) rotate(180deg);
        border-top-color: ${COLORS.primary.teal};
        opacity: 1;
      }
      .navbar-marine .nav-link::after {
        content: '';
        display: block;
        width: 0;
        height: 2px;
        background: ${COLORS.primary.teal};
        transition: width 0.3s;
        position: absolute;
        left: 0;
        bottom: -4px;
      }
      .navbar-marine .nav-link:hover,
      .navbar-marine .nav-link:focus {
        color: ${COLORS.primary.teal} !important;
      }
      .navbar-marine .nav-link:hover::after,
      .navbar-marine .nav-link:focus::after {
        width: 100%;
      }
      .navbar-marine .navbar-toggler {
        border-color: ${COLORS.primary.teal};
      }
      .navbar-marine .navbar-toggler-icon {
        background-image: url("data:image/svg+xml;charset=utf8,%3Csvg viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='rgba(64,168,196, 1)' stroke-width='2' stroke-linecap='round' stroke-miterlimit='10' d='M4 7h22M4 15h22M4 23h22'/%3E%3C/svg%3E");
      }
      .navbar-marine .dropdown:hover .dropdown-menu {
        display: block;
        margin-top: 0;
        animation: dropdownFadeIn 0.3s ease;
      }
      .navbar-marine .dropdown-menu {
        margin-top: ${SPACING.xs};
        border: none;
        border-radius: ${RADIUS.xl};
        box-shadow: ${SHADOWS.lg};
        background: ${COLORS.background.white};
        padding: ${SPACING.sm} 0;
        min-width: 200px;
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        position: relative;
      }
      .navbar-marine .dropdown-menu::before {
        content: '';
        position: absolute;
        top: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-bottom: 8px solid ${COLORS.background.white};
      }
      .navbar-marine .dropdown-item {
        color: ${COLORS.primary.dark};
        font-weight: ${TYPOGRAPHY.fontWeight.medium};
        font-size: ${TYPOGRAPHY.fontSize.base};
        padding: ${SPACING.sm} ${SPACING.lg};
        transition: ${TRANSITIONS.all};
        position: relative;
        border-radius: 0;
      }
      .navbar-marine .dropdown-item::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 4px;
        background: ${COLORS.primary.teal};
        transform: scaleY(0);
        transition: transform 0.3s ease;
      }
      .navbar-marine .dropdown-item:hover,
      .navbar-marine .dropdown-item:focus {
        background: linear-gradient(90deg, rgba(64,168,196,0.1) 0%, transparent 100%);
        color: ${COLORS.primary.teal};
        transform: translateX(8px);
      }
      .navbar-marine .dropdown-item:hover::before,
      .navbar-marine .dropdown-item:focus::before {
        transform: scaleY(1);
      }
      .navbar-marine .dropdown-divider {
        margin: ${SPACING.xs} ${SPACING.md};
        border-color: rgba(64, 168, 196, 0.2);
        opacity: 1;
      }
      @keyframes dropdownFadeIn {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      /* Mobile dropdown improvements */
      @media (max-width: 991px) {
        .navbar-marine .dropdown-menu {
          background: rgba(8, 32, 50, 0.95);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: ${RADIUS.md};
          margin: ${SPACING.xs} 0;
          box-shadow: inset 0 1px 0 rgba(64, 168, 196, 0.3);
        }
        .navbar-marine .dropdown-menu::before {
          display: none;
        }
        .navbar-marine .dropdown-item {
          color: ${COLORS.text.white};
          border-left: 3px solid transparent;
        }
        .navbar-marine .dropdown-item::before {
          display: none;
        }
        .navbar-marine .dropdown-item:hover,
        .navbar-marine .dropdown-item:focus {
          background: rgba(64, 168, 196, 0.2);
          color: ${COLORS.primary.teal};
          border-left-color: ${COLORS.primary.teal};
          transform: none;
        }
        .navbar-marine .dropdown-divider {
          border-color: rgba(64, 168, 196, 0.3);
        }
      }
    `}</style>
    <nav className={`navbar navbar-expand-lg navbar-marine ${scrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <a className="navbar-brand fw-bold" href="/"><img src="/img/Logo-Full_White.png" width="140" alt="Marine Sealink B.V. logo"/>
        </a>
        <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav ms-auto">
            <li className="nav-item">
              <a className="nav-link" href="/">Home</a>
            </li>
            <li className="nav-item">
              <a className="nav-link" href="/#about">About</a>
            </li>
            <li className="nav-item dropdown">
              <a className="nav-link dropdown-toggle" href="/#services" id="sparesDropdown" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                Spare Parts
              </a>
              <ul className="dropdown-menu" aria-labelledby="sparesDropdown">
                {CATEGORIES.map((cat) => (
                  <li key={cat.key}>
                    <a className="dropdown-item" href={cat.path}>{cat.title}</a>
                  </li>
                ))}
              </ul>
            </li>
            <li className="nav-item">
              <a className="nav-link" href="/#contact">Contact</a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </>
  );
};

export default Navbar;
